// Patches XMLHttpRequest.prototype so every instance, including ones created by
// libraries such as jQuery and Axios, is recorded.

import { absoluteUrl, bodySummary, graphqlErrors, parseRawHeaders, shortUrl } from '../net-utils.js';

export function installXhr(ctx, win = window) {
  const XHR = win.XMLHttpRequest;
  if (!XHR) return;
  const proto = XHR.prototype;
  const { open, send, setRequestHeader } = proto;
  const meta = new WeakMap();
  const max = ctx.config.maxBodyLength;

  proto.open = function (method, url) {
    try {
      meta.set(this, {
        method: String(method || 'GET').toUpperCase(),
        url: absoluteUrl(url),
        headers: {},
      });
    } catch {
      meta.delete(this);
    }
    return open.apply(this, arguments);
  };

  proto.setRequestHeader = function (name, value) {
    const m = meta.get(this);
    if (m) m.headers[String(name).toLowerCase()] = String(value);
    return setRequestHeader.apply(this, arguments);
  };

  // Listeners are added once per instance. They read the current request's meta,
  // because sites can reuse one XMLHttpRequest for several requests (open() resets it).
  const listening = new WeakSet();
  const listen = (xhr) => {
    if (listening.has(xhr)) return;
    listening.add(xhr);
    const onFailure = (event) => {
      const m = meta.get(xhr);
      if (m) m.failure = event.type;
    };
    xhr.addEventListener('error', onFailure);
    xhr.addEventListener('timeout', onFailure);
    xhr.addEventListener('abort', onFailure);
    xhr.addEventListener('loadend', () => {
      const m = meta.get(xhr);
      if (!m || !m.tracked || m.recorded) return;
      m.recorded = true;
      try {
        record(xhr, m);
      } catch {
        // never break the page's request handling
      }
    });
  };

  proto.send = function (body) {
    const m = meta.get(this);
    if (m && !ctx.busy && !ctx.isOwnUrl(m.url)) {
      m.tracked = true;
      m.startedAt = Date.now();
      m.t0 = performance.now();
      m.request = bodySummary(body, max);
      listen(this);
    }
    return send.apply(this, arguments);
  };

  function readBody(xhr) {
    const type = xhr.responseType;
    try {
      if (type === '' || type === 'text') {
        const text = xhr.responseText || '';
        return { body: text.slice(0, max), truncated: text.length > max || undefined };
      }
      if (type === 'json') {
        const text = JSON.stringify(xhr.response);
        return { body: text ? text.slice(0, max) : '', truncated: (text && text.length > max) || undefined };
      }
      if (type === 'arraybuffer' && xhr.response) return { note: `[binary ${xhr.response.byteLength} bytes]` };
      if (type === 'blob' && xhr.response) return { note: `[Blob ${xhr.response.type} ${xhr.response.size} bytes]` };
      if (type === 'document') return { note: '[Document response]' };
    } catch (error) {
      return { note: `[body not readable: ${error && error.message}]` };
    }
    return {};
  }

  function record(xhr, m) {
    const status = xhr.status;
    const headers = parseRawHeaders(xhr.getAllResponseHeaders());
    const response = { headers: ctx.redact.headers(headers), contentType: headers['content-type'] };
    const failed = status === 0;
    if (!failed) {
      const read = readBody(xhr);
      Object.assign(response, read, { body: ctx.redact.body(read.body) });
    }
    const detail = {
      url: ctx.redact.url(m.url),
      method: m.method,
      initiator: 'xhr',
      startedAt: m.startedAt,
      duration: Math.round(performance.now() - m.t0),
      status,
      statusText: xhr.statusText || undefined,
      ok: status >= 200 && status < 300,
      request: {
        headers: ctx.redact.headers(m.headers),
        ...ctx.redact.summary(m.request),
      },
      response,
    };
    if (failed) {
      detail.failed = true;
      detail.error = m.failure || 'Request failed (status 0)';
      if (m.failure === 'abort') detail.aborted = true;
    }
    const gql = detail.ok ? graphqlErrors(response.body) : null;
    if (gql) detail.graphqlErrors = gql;

    const clean = ctx.sanitise('network', detail);
    if (!clean) return;
    let level = 'info';
    let label = String(status);
    if (failed) {
      level = m.failure === 'abort' ? 'warn' : 'error';
      label = m.failure === 'abort' ? 'aborted' : m.failure === 'timeout' ? 'timed out' : 'failed';
    } else if (status >= 500) level = 'error';
    else if (status >= 400 || gql) level = 'warn';
    ctx.push(
      'network',
      level,
      `${m.method} ${shortUrl(ctx.redact.url(m.url))} ${label}${gql ? ' (GraphQL errors)' : ''}`,
      clean,
      m.startedAt,
    );
  }
}
